import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  RadioGroup,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { IndustryType } from '../../client/IvtHubClient';
import { createIndustry } from '../../client/industryAdminClient';
import { denialHint } from '../../utils/authorization';
import { INDUSTRY_CODE } from './industryAdmin';

/**
 * Register a new industry by code and type. It starts with no nodes; they are set on the
 * Industry mapping tab. The code is also the name people see, and never changes.
 */
export function AddIndustryModal({
  existingIndustries,
  onDismiss,
  onCreated,
}: {
  existingIndustries: readonly string[];
  onDismiss: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<IndustryType | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = code.trim();
  // Codes are unique case-insensitively, as the service compares them.
  const taken = existingIndustries.some((i) => i.toLowerCase() === trimmed.toLowerCase());
  const codeError =
    trimmed === ''
      ? undefined
      : !INDUSTRY_CODE.test(trimmed)
        ? 'Letters, digits, _ and - only, starting with a letter or digit.'
        : taken
          ? `${trimmed} already exists.`
          : undefined;
  const canSubmit = trimmed !== '' && !codeError && !!type && !pending;

  const submit = async () => {
    if (!canSubmit || !type) return;
    setPending(true);
    setError(null);
    try {
      await createIndustry(trimmed, type);
      onCreated();
    } catch (e) {
      setError(
        (e as { name?: string } | null)?.name === 'ConflictException'
          ? `${trimmed} already exists. Reload the page to see it.`
          : (denialHint(e, 'en') ?? (e as Error)?.message ?? 'Could not add the industry.'),
      );
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      visible
      // Not dismissable mid-request: the outcome would have nowhere to be shown.
      onDismiss={() => {
        if (!pending) onDismiss();
      }}
      header="Add industry"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={pending}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit} loading={pending} onClick={() => void submit()}>
              Add
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label="Industry code"
          description="Also the name people see. It cannot be changed later."
          errorText={codeError}
        >
          <Input
            value={code}
            onChange={({ detail }) => setCode(detail.value)}
            placeholder="e.g. AUTO"
            disabled={pending}
          />
        </FormField>
        <FormField label="Industry type">
          <RadioGroup
            direction="horizontal"
            value={type}
            onChange={({ detail }) => setType(detail.value as IndustryType)}
            items={[
              { value: IndustryType.VERTICAL, label: 'Vertical' },
              { value: IndustryType.HORIZONTAL, label: 'Horizontal' },
            ]}
          />
        </FormField>
        {error && (
          <Alert type="error" header="Could not add">
            {error}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
